import React, { useState } from 'react';
import styled from 'styled-components';
import { Grid, Type } from '../../../app/StyleGuide';
import { ListItemFlexRow } from '../../../components/ListItemFlexRow';
import { CircleCheck } from '../../../components/FormElements';
import { PrimaryButton } from '../../../components/Buttons';
import { Category } from '../models/categories';
import { submitDisciplesPathInquiry } from '../api/submitDisciplesPathInquiry';
import { ErrorMessage } from '../../../components/AlertMessages';
import alertService from '../../../services/AlertService';
import windowService from '../../../services/windowService';

const categories: Category[] = [
  { name: 'Curriculum for Preschool, Kids, Students or Adults', value: 'ongoing' },
  { name: 'Bible Studies for Small Groups', value: 'short term' },
  { name: "Women's Ministry Studies and Events", value: 'women' },
  { name: 'Vacation Bible School', value: 'vbs' },
  { name: 'Bibles', value: 'bibles' },
];

const ListContainer = styled.ul`
  padding: 0;
  list-style: none;
  width: 100%;
  margin-bottom: ${Grid._6};

  ${ListItemFlexRow} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: ${Grid._5} ${Grid._4};

    h4 {
      margin: 0;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Grid._5};
  max-width: 800px;

  h3 {
    font-weight: ${Type.Weight.normal};
  }
  h4 {
    align-self: flex-start;
  }

  img {
    max-width: 350px;
  }
`;

export const DisciplesPathView = () => {
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<boolean>(false);

  const toggleSelection = (category: Category, isSelected: boolean) => {
    if (isSelected) return setSelectedCategories(prev => prev.filter(selected => selected.name !== category.name));
    return setSelectedCategories(prev => [...prev, category]);
  };

  const onSubmit = () => {
    setIsSubmitting(true);
    return submitDisciplesPathInquiry(selectedCategories.map(category => category.value))
      .then(() => {
        alertService.showOnNextPage('Thank you for your request! A Lifeway team member will reach out to you soon.');
        windowService.redirectTo('/dashboard/training');
      })
      .catch(err => {
        setIsSubmitting(false);
        const errorType = err.response?.data?.error;
        if (errorType === 'Organization has no linked church account') {
          setError(true);
        } else {
          alertService.showError();
        }
      });
  };

  return (
    <Content>
      <img src="img/disciples-path.png" alt="Disciples Path" />
      <h3>
        Please select one or more categories of interest. These categories help us better understand what your church or
        ministry needs are. A Lifeway team member will reach out to you soon.
      </h3>
      <h4>Select all that apply</h4>
      <ListContainer>
        {categories.map((category: Category) => {
          const selected = !!selectedCategories.find(selected => selected.name === category.name);
          return (
            <ListItemFlexRow key={category.name} onClick={() => toggleSelection(category, selected)}>
              <h4>{category.name}</h4>
              <CircleCheck checked={selected} />
            </ListItemFlexRow>
          );
        })}
      </ListContainer>
      {error && (
        <ErrorMessage>
          <span>
            This action requires syncing between your Ministry Grid church and your Lifeway church account.{' '}
            <a href="#/org">Go to Church Management</a> to begin syncing.
          </span>
        </ErrorMessage>
      )}
      <PrimaryButton
        disabled={selectedCategories.length === 0 || isSubmitting || error}
        onClick={onSubmit}
        operating={isSubmitting}
      >
        Submit
      </PrimaryButton>
    </Content>
  );
};
